import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { validateOTP } from "../auth/validationHelpers";
import { toastStyle } from "../../utils/toastUtils";
import cn from "../../utils/cn";

const RESEND_SECONDS = 60;
const CODE_LENGTH = 6;
const emptyDigits = () => Array(CODE_LENGTH).fill("");

// Step 2 of login and sign-up. onVerify and onResend return an error message, or nothing when they succeed.
function OtpStep({ email, onVerify, onResend, onBack, verifyLabel = "Verify", verifyingLabel = "Verifying" }) {
  const [digits, setDigits] = useState(emptyDigits);
  const boxRefs = useRef([]);
  const otp = digits.join("");
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;
    const timeout = setTimeout(() => setResendTimer((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timeout);
  }, [resendTimer]);

  const focusBox = (index) => {
    const box = boxRefs.current[Math.max(0, Math.min(index, CODE_LENGTH - 1))];
    box?.focus();
    box?.select();
  };

  // Writes digits from index onward, so typing, pasting and phone autofill all land in the boxes
  const fillFrom = (index, text) => {
    const incoming = text.replace(/\D/g, "").slice(0, CODE_LENGTH - index).split("");
    if (!incoming.length) return;
    setDigits((prev) => {
      const next = [...prev];
      incoming.forEach((digit, offset) => {
        next[index + offset] = digit;
      });
      return next;
    });
    setError("");
    focusBox(index + incoming.length);
  };

  const handleChange = (index) => (e) => {
    const { value } = e.target;
    if (value === "") {
      setDigits((prev) => prev.map((digit, i) => (i === index ? "" : digit)));
      return;
    }
    // Typing into a filled box: keep only the key just pressed. Autofill has no single key, so it uses the whole value.
    const typed = e.nativeEvent?.data;
    fillFrom(index, typed && typed.length === 1 ? typed : value);
  };

  const handleKeyDown = (index) => (e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      setDigits((prev) => prev.map((digit, i) => (i === index - 1 ? "" : digit)));
      focusBox(index - 1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusBox(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index) => (e) => {
    e.preventDefault();
    // A full code always starts at the first box, wherever the cursor is
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
    fillFrom(pasted.length >= CODE_LENGTH ? 0 : index, pasted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpError = validateOTP(otp);
    if (otpError) {
      setError(otpError);
      return;
    }
    setIsVerifying(true);
    const message = await onVerify(otp);
    // A successful verify navigates away, so only a failure updates this step
    if (message) {
      setError(message);
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || isResending) return;
    setIsResending(true);
    const message = await onResend();
    setIsResending(false);
    if (message) {
      toast.error(message, { style: toastStyle });
      return;
    }
    setDigits(emptyDigits());
    setError("");
    setResendTimer(RESEND_SECONDS);
    focusBox(0);
    toast.success("We sent you a new code.", { style: toastStyle });
  };

  const resendDisabled = resendTimer > 0 || isResending || isVerifying;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 min-w-0">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-black">Check your email</h1>
        <p className="text-sm text-accent-gray-medium mt-2">
          We sent a 6-digit code to <span className="font-semibold text-black">{email}</span>
        </p>
      </div>

      <div>
        <label htmlFor="otp-0" className="block text-md font-bold leading-5 text-black">
          Verification code
        </label>
        <div className="mt-2 grid grid-cols-6 gap-2 sm:gap-3">
          {digits.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              ref={(el) => {
                boxRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : "off"}
              aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
              value={digit}
              onChange={handleChange(index)}
              onKeyDown={handleKeyDown(index)}
              onPaste={handlePaste(index)}
              onFocus={(e) => e.target.select()}
              autoFocus={index === 0}
              disabled={isVerifying}
              className={cn(
                "h-12 sm:h-14 w-full min-w-0 rounded-md border bg-white text-center text-xl font-semibold text-black focus:outline-none focus:border-accent-blue transition duration-150 ease-in-out",
                error ? "border-accent-red" : "border-accent-gray-border"
              )}
            />
          ))}
        </div>
        {error && <p className="text-accent-red text-sm mt-1">{error}</p>}
        <div className="mt-2 flex items-center justify-between gap-3 text-sm">
          <button type="button" onClick={onBack} disabled={isVerifying} className="text-accent-blue hover:underline">
            Use a different email
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendDisabled}
            className={cn(resendDisabled ? "text-accent-gray cursor-not-allowed" : "text-accent-blue hover:underline")}
          >
            {resendTimer > 0 ? `Resend code in ${resendTimer}s` : isResending ? "Sending..." : "Resend code"}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={isVerifying}
        className={cn(
          "w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent text-base font-semibold rounded-md text-white transition duration-150 ease-in-out bg-accent-blue",
          isVerifying ? "cursor-not-allowed" : "hover:bg-accent-blue-hover"
        )}
      >
        {isVerifying ? (
          <>
            {verifyingLabel}
            <div className="animate-spin rounded-full border-t-2 border-white h-4 w-4"></div>
          </>
        ) : (
          verifyLabel
        )}
      </button>
    </form>
  );
}

export default OtpStep;
